/**
 * settlementUtils — pure Helfer der Settlement-UI (Stories 2.5 + 2.6)
 *
 * Bewusst ohne React: separat unit-testbar (settlementUtils.test.ts).
 */
import { formatNumber } from '../../components/ui/format'
import type { Settlement, SettlementStatus, SettlementTotal } from './useSettlements'

export const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$',
  EUR: '€',
  HNT: 'HNT',
}

/**
 * Nachkommastellen je Waehrung.
 *
 * REVIEW-FIX Geld/Steuer: HNT hat 8 Dezimalstellen und wird von API, PDF, CSV
 * und QR-Code auch mit 8 Stellen gefuehrt. Mit den bisherigen 2 Stellen
 * bestaetigte der Betreiber im Payout-Modal eine ANDERE Zahl, als der
 * QR-Code ueberweist (0,00000001 HNT wurde als „0,00 HNT" angezeigt).
 * Fiat bleibt bei 2 Stellen.
 */
export const HNT_DECIMALS = 8
export const FIAT_DECIMALS = 2

export function decimalsForCurrency(currency: string | undefined): number {
  return currency === 'HNT' ? HNT_DECIMALS : FIAT_DECIMALS
}

/** Betrag "12,50 $" / "3,75000000 HNT" — null bei fehlendem/unlesbarem Wert. */
export function fmtAmount(
  raw: string | undefined,
  currency: string | undefined,
  decimals?: number,
): string | null {
  if (raw === undefined || raw === null) return null
  const value = Number(raw)
  if (!Number.isFinite(value)) return null
  const symbol = currency ? (CURRENCY_SYMBOL[currency] ?? currency) : ''
  const num = formatNumber(value, decimals ?? decimalsForCurrency(currency))
  return symbol ? `${num} ${symbol}` : num
}

/** Dezimal-String mit fester Stellenzahl — null bei fehlendem Wert. */
export function fmtDecimal(raw: string | undefined, decimals = 2): string | null {
  if (raw === undefined || raw === null) return null
  const value = Number(raw)
  if (!Number.isFinite(value)) return null
  return formatNumber(value, decimals)
}

/** "2026-07" → lokalisiert "Juli 2026" — bei unerwartetem Format Rohwert. */
export function fmtPeriod(period: string | undefined, locale: string): string | null {
  if (!period) return null
  const match = /^(\d{4})-(\d{2})$/.exec(period)
  if (!match) return period
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

/**
 * "2026-07" → "JUL" (Design-Angleichung: farbiges Monats-Kuerzel als
 * Zeilen-Icon, Vorlage payout-commissions.html `identity-img`). Rein
 * kosmetisch, keine neuen Daten — bei unerwartetem Format "—".
 */
export function monthAbbrev(period: string | undefined, locale: string): string {
  const match = period ? /^(\d{4})-(\d{2})$/.exec(period) : null
  if (!match) return '—'
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
  return new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
    .format(date)
    .replace('.', '')
    .toUpperCase()
}

/** Zyklischer Farbton fuer den Monats-Badge, stabil je Kalendermonat (0-11). */
const MONTH_TONES = ['primary', 'accent', 'success', 'warning', 'info', 'danger'] as const

export function monthTone(period: string | undefined): (typeof MONTH_TONES)[number] {
  const match = period ? /^\d{4}-(\d{2})$/.exec(period) : null
  const monthIndex = match ? Number(match[1]) - 1 : 0
  return MONTH_TONES[monthIndex % MONTH_TONES.length]
}

/**
 * Vormonat des uebergebenen Stichtags als YYYY-MM — der Default-Zeitraum von
 * POST /api/v2/settlements/generate ("Default = Vormonat des Stichtags").
 */
export function previousPeriodMonth(now: Date): string {
  const year = now.getUTCFullYear()
  const month = now.getUTCMonth() // 0-basiert; getUTCMonth() selbst IST der Vormonat in 1-basierter Zaehlung
  const prevYear = month === 0 ? year - 1 : year
  const prevMonth = month === 0 ? 12 : month
  return `${prevYear}-${String(prevMonth).padStart(2, '0')}`
}

/**
 * Naechster Schritt der Status-Kette draft → approved → settled.
 * settled ist final (Contract: PATCH mit unerlaubtem Wechsel → 422).
 */
export function nextStatus(status: SettlementStatus | undefined): SettlementStatus | null {
  if (status === 'draft') return 'approved'
  if (status === 'approved') return 'settled'
  return null
}

/**
 * Summen je Waehrung fuer einen Status, z. B. ["9,10 $", "1,20 HNT"].
 * Waehrungsgetrennt, weil HNT/EUR/USD ohne Kurs nicht addierbar sind
 * (Contract-Kommentar zu SettlementTotal).
 */
export function sumsForStatus(
  totals: SettlementTotal[] | undefined,
  status: SettlementStatus,
): string[] {
  const byCurrency = new Map<string, number>()
  for (const total of totals ?? []) {
    if (total.status !== status) continue
    const value = Number(total.total_amount ?? '')
    if (!Number.isFinite(value)) continue
    const cur = total.currency ?? ''
    byCurrency.set(cur, (byCurrency.get(cur) ?? 0) + value)
  }
  return [...byCurrency.entries()].map(([cur, value]) => {
    const symbol = CURRENCY_SYMBOL[cur] ?? cur
    return `${formatNumber(value, decimalsForCurrency(cur))} ${symbol}`
  })
}

/**
 * Rein dekorativer Nutzungsbalken unter der Basis-Zelle (Vorlage `.comp`,
 * dort `Math.min(100, gb/2)`). Skala ist bewusst willkuerlich (kein Quota-
 * Feld im Contract) — nur zur visuellen Auflockerung, keine echte Kennzahl.
 */
export function usagePct(gigabytes: string | undefined): number {
  const value = Number(gigabytes ?? '')
  if (!Number.isFinite(value) || value <= 0) return 0
  return Math.min(100, value / 2)
}

/** true, wenn das Settlement einen QR-/Wallet-Payload hat (Story 2.6, nil-sicher). */
export function hasWallet(settlement: Settlement | undefined): boolean {
  return !!settlement?.payout?.wallet_address
}

/**
 * Fix Abnahme-Finding P1 (fehlende RATE-Spalte, settlements.html): der
 * Contract liefert keinen eigenstaendigen Rate-Wert, wohl aber
 * amount_from_gigabytes UND gigabytes_transferred — beides ECHTE Felder
 * derselben Zeile. Der Quotient ist der tatsaechlich angewandte GB-Satz
 * dieses Settlements, keine erfundene Zahl (anders als total_amount/GB,
 * das die HNT-Basis mischen wuerde, siehe Kopf-Kommentar SettlementsPage.tsx).
 * null, wenn die Regeln des Settlements keine GB-Basis nutzen (gb=0).
 */
export function fmtRatePerGb(settlement: Settlement): string | null {
  const gb = Number(settlement.gigabytes_transferred ?? '')
  const amount = Number(settlement.amount_from_gigabytes ?? '')
  if (!Number.isFinite(gb) || gb <= 0 || !Number.isFinite(amount)) return null
  const symbol = settlement.currency ? (CURRENCY_SYMBOL[settlement.currency] ?? settlement.currency) : ''
  return `${symbol}${formatNumber(amount / gb, 3)}/GB`
}
